import { PlusIcon } from '@blocksuite/icons/lit';
import { addDays } from 'date-fns/addDays';
import { getISOWeek } from 'date-fns/getISOWeek';
import { startOfWeek } from 'date-fns/startOfWeek';
import { html, type TemplateResult } from 'lit';

import {
  dataViewI18nT,
  getDataViewRuntimeLocale,
} from '../../../../core/i18n.js';
import { TEMPORAL_TOOLBAR_ICON_SIZE } from '../../../shared/toolbar/temporal-toolbar.js';
import type { CalendarMode } from '../../define.js';
import { calendarRegionMotion } from '../calendar-chrome-motion.js';
import { styles } from '../calendar-view-styles.js';

export function renderCalendarIcsFeedForm({
  onCancel,
  onInput,
  onSubmit,
  open,
  readonly,
  url,
}: {
  onCancel: () => void;
  onInput: (value: string) => void;
  onSubmit: (url: string) => void;
  open: boolean;
  readonly: boolean;
  url: string;
}): TemplateResult {
  return html`<div ${calendarRegionMotion(open)} data-calendar-feed-region>
    <form
      class="${styles.feedForm}"
      @submit=${(event: SubmitEvent) => {
        event.preventDefault();
        const input = (
          event.currentTarget as HTMLFormElement
        ).querySelector<HTMLInputElement>('input[type="url"]')!;
        if (open && !readonly) onSubmit(input.value);
      }}
      @keydown=${(event: KeyboardEvent) => {
        if (event.key === 'Escape') onCancel();
      }}
    >
      <label class="${styles.feedField}">
        <span>${dataViewI18nT('calendar.toolbar.feedUrl')}</span>
        <input
          type="url"
          inputmode="url"
          placeholder="https://example.com/calendar.ics"
          .value=${url}
          ?disabled=${readonly || !open}
          @input=${(event: InputEvent) =>
            onInput((event.currentTarget as HTMLInputElement).value)}
        />
      </label>
      <button
        class="${styles.primaryButton}"
        type="submit"
        ?disabled=${readonly || !open}
      >
        ${PlusIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
        <span>${dataViewI18nT('calendar.action.add')}</span>
      </button>
      <button class="${styles.todayButton}" type="button" @click=${onCancel}>
        ${dataViewI18nT('calendar.action.cancel')}
      </button>
    </form>
  </div>`;
}

export function renderCalendarRangeTitle(
  mode: CalendarMode,
  cursor: Date
): string {
  if (mode === 'day') {
    return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
      day: 'numeric',
      month: 'short',
      weekday: 'short',
      year: 'numeric',
    }).format(cursor);
  }
  if (mode === 'week') {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    const end = addDays(start, 6);
    const formatter = new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const range =
      typeof formatter.formatRange === 'function'
        ? formatter.formatRange(start, end)
        : `${formatter.format(start)} - ${formatter.format(end)}`;
    return `${range} (KW ${getISOWeek(start)})`;
  }
  return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
    month: 'long',
    year: 'numeric',
  }).format(cursor);
}
